import React, { useEffect, useMemo, useState } from "react";
import jwtDecode from "jwt-decode";
import Translator from "./translator";
import { formatMontant } from "../utils/formatting.js";
import { getDepotRequisByIdProfilePav, getSoldeDepotRequisByIdProfilePav } from "../server/services/client-service.js";
import "../../css/profile-historique-achats-tab.css";
import "../../css/profile-depots-tab.css";

function normalizeSoldeDepot(raw) {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw))
    return { depotRequis: 0, soldeDu: 0 };

  const depotRequis = Number(raw.depotRequis ?? raw.DepotRequis ?? 0);
  const soldeDu = Number(raw.soldeDu ?? raw.SoldeDu ?? 0);

  return {
    depotRequis: Number.isFinite(depotRequis) ? depotRequis : 0,
    soldeDu: Number.isFinite(soldeDu) ? soldeDu : 0,
  };
}

function normalizeDepotRow(raw, index) {
  const montant = Number(raw.montant ?? raw.Montant ?? 0);
  const noConfirmation = raw.noConfirmation ?? raw.no_confirmation ?? raw.NoConfirmation ?? "—";
  const noDepot = raw.noDepot ?? raw.no_depot ?? raw.NoDepot ?? "";
  const dateRaw = raw.dateDelai ?? raw.date_delai ?? raw.DateDelai ?? null;

  return {
    id: String(noConfirmation) + "\0" + String(noDepot) + "\0" + String(index),
    noConfirmation: String(noConfirmation),
    noDepot: String(noDepot),
    montant: Number.isFinite(montant) ? montant : 0,
    dateRaw,
  };
}

function formatDepotDate(dateRaw, language) {
  if (dateRaw == null || dateRaw === "")
    return "—";

  try {
    const s = typeof dateRaw === "string" ? dateRaw : String(dateRaw);
    const d = new Date(s.length >= 10 ? s.substring(0, 10) : s);
    if (Number.isNaN(d.getTime()))
      return "—";

    return d.toLocaleDateString(language === "en" ? "en-CA" : "fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}

const SORT_RESERVATION = "reservation";
const SORT_DEPOT = "depot";
const SORT_DATE = "date";
const SORT_MONTANT = "montant";

function compareLocale(a, b, language) {
  return String(a).localeCompare(String(b), language === "en" ? "en-CA" : "fr-CA", { sensitivity: "base", numeric: true });
}

function sortDepotsRows(rows, sortKey, sortDir, language) {
  const mult = sortDir === "asc" ? 1 : -1;

  return [...rows].sort((a, b) => {
    if (sortKey === SORT_RESERVATION)
      return mult * compareLocale(a.noConfirmation, b.noConfirmation, language);

    if (sortKey === SORT_DEPOT)
      return mult * compareLocale(a.noDepot, b.noDepot, language);

    if (sortKey === SORT_DATE) {
      const ta = a.dateRaw ? new Date(String(a.dateRaw).substring(0, 10)).getTime() : 0;
      const tb = b.dateRaw ? new Date(String(b.dateRaw).substring(0, 10)).getTime() : 0;

      return mult * (ta - tb);
    }

    if (sortKey === SORT_MONTANT)
      return mult * (a.montant - b.montant);

    return 0;
  });
}

function defaultDirForSortKey(sortKey) {
  if (sortKey === SORT_RESERVATION || sortKey === SORT_DEPOT)
    return "asc";

  return "desc";
}

export default function ProfileDepotsTab({ language = "fr", idZec, authToken, setAlert }) {
  const TEXTS = useMemo(() => Translator(language, "TXT_PROFILE_PAGE"), [language]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [solde, setSolde] = useState({ depotRequis: 0, soldeDu: 0 });
  const [lignes, setLignes] = useState([]);
  const [sortKey, setSortKey] = useState(SORT_DATE);
  const [sortDir, setSortDir] = useState("desc");

  const lignesTriees = useMemo(
    () => sortDepotsRows(lignes, sortKey, sortDir, language),
    [lignes, sortKey, sortDir, language],
  );

  const onTriColonne = (key) => {
    if (key === sortKey)
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(defaultDirForSortKey(key));
    }
  };

  useEffect(() => {
    if (!authToken || !idZec) {
      setLoading(false);
      setLignes([]);
      setSolde({ depotRequis: 0, soldeDu: 0 });
      return;
    }

    let cancelled = false;

    setLoading(true);
    setError(null);

    let decoded;
    try {
      decoded = jwtDecode(authToken);
    } catch {
      setLoading(false);
      setError(TEXTS.depotsErreur);
      return;
    }

    const idProfile = decoded.idProfile;

    Promise.all([
      getDepotRequisByIdProfilePav(idProfile),
      getSoldeDepotRequisByIdProfilePav(idProfile),
    ])
      .then(([depotsRequis, soldeDepotRequis]) => {
        if (cancelled)
          return;

        if (!Array.isArray(depotsRequis)) {
          setError(TEXTS.depotsErreur);
          setLignes([]);
          setSolde({ depotRequis: 0, soldeDu: 0 });
          if (typeof setAlert === "function")
            setAlert(TEXTS.depotsErreur, "danger");
          return;
        }

        setError(null);
        setSolde(normalizeSoldeDepot(soldeDepotRequis));
        setLignes(depotsRequis.map(normalizeDepotRow));
      })
      .catch(() => {
        if (cancelled)
          return;

        setError(TEXTS.depotsErreur);
        setLignes([]);
        setSolde({ depotRequis: 0, soldeDu: 0 });
        if (typeof setAlert === "function")
          setAlert(TEXTS.depotsErreur, "danger");
      })
      .finally(() => {
        if (!cancelled)
          setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [authToken, idZec, language, setAlert, TEXTS]);

  return (
    <div className="profile-depots-tab profile-historique-achats-tab" data-language={language || ""}>
      <h2 className="profile-historique-achats-title">
        <span className="profile-span">{TEXTS.depotsTitre}</span>
      </h2>

      {loading && (
        <p className="profile-historique-achats-message profile-historique-achats-message--muted">
          {TEXTS.depotsChargement}
        </p>
      )}

      {!loading && error && !lignes.length && (
        <p className="profile-historique-achats-message profile-historique-achats-message--error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && (
        <div className="profile-depots-summary" aria-label={TEXTS.depotsResumeAria}>
          <div className="profile-depots-summary-row">
            <span className="profile-span profile-depots-summary-label">{TEXTS.depotsTotalDepotRequis}</span>
            <span className="profile-span profile-depots-summary-value">{formatMontant(solde.depotRequis, language)}</span>
          </div>
          <div className="profile-depots-summary-row">
            <span className="profile-span profile-depots-summary-label">{TEXTS.depotsSoldeDu}</span>
            <span className="profile-span profile-depots-summary-value">{formatMontant(solde.soldeDu, language)}</span>
          </div>
        </div>
      )}

      {!loading && !error && lignes.length === 0 && (
        <p className="profile-historique-achats-message profile-historique-achats-message--muted">
          {TEXTS.depotsVide}
        </p>
      )}

      {!loading && !error && lignes.length > 0 && (
        <>
          <h3 className="profile-depots-sous-titre">
            <span className="profile-span">{TEXTS.depotsListeTitre}</span>
          </h3>
          <div className="profile-historique-achats-table-wrap">
            <table className="profile-historique-achats-table">
              <thead>
                <tr>
                  <th
                    scope="col"
                    aria-sort={sortKey === SORT_RESERVATION ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      type="button"
                      className={
                        "profile-historique-achats-sort" +
                        (sortKey === SORT_RESERVATION ? " profile-historique-achats-sort--active" : "")
                      }
                      onClick={() => onTriColonne(SORT_RESERVATION)}
                      title={TEXTS.depotsColReservation}
                      aria-label={TEXTS.depotsColReservation}
                    >
                      <span>{TEXTS.depotsColReservation}</span>
                      <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                        <span
                          className={
                            "profile-historique-achats-sort-icon-char" +
                            (sortKey === SORT_RESERVATION ? "" : " profile-historique-achats-sort-icon-char--reserve")
                          }
                        >
                          {sortKey === SORT_RESERVATION ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                        </span>
                      </span>
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={sortKey === SORT_DEPOT ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      type="button"
                      className={
                        "profile-historique-achats-sort" +
                        (sortKey === SORT_DEPOT ? " profile-historique-achats-sort--active" : "")
                      }
                      onClick={() => onTriColonne(SORT_DEPOT)}
                      title={TEXTS.depotsColDepot}
                      aria-label={TEXTS.depotsColDepot}
                    >
                      <span>{TEXTS.depotsColDepot}</span>
                      <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                        <span
                          className={
                            "profile-historique-achats-sort-icon-char" +
                            (sortKey === SORT_DEPOT ? "" : " profile-historique-achats-sort-icon-char--reserve")
                          }
                        >
                          {sortKey === SORT_DEPOT ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                        </span>
                      </span>
                    </button>
                  </th>
                  <th
                    scope="col"
                    aria-sort={sortKey === SORT_DATE ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      type="button"
                      className={
                        "profile-historique-achats-sort" +
                        (sortKey === SORT_DATE ? " profile-historique-achats-sort--active" : "")
                      }
                      onClick={() => onTriColonne(SORT_DATE)}
                      title={TEXTS.depotsColEcheance}
                      aria-label={TEXTS.depotsColEcheance}
                    >
                      <span>{TEXTS.depotsColEcheance}</span>
                      <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                        <span
                          className={
                            "profile-historique-achats-sort-icon-char" +
                            (sortKey === SORT_DATE ? "" : " profile-historique-achats-sort-icon-char--reserve")
                          }
                        >
                          {sortKey === SORT_DATE ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                        </span>
                      </span>
                    </button>
                  </th>
                  <th
                    scope="col"
                    className="profile-historique-achats-th-numeric"
                    aria-sort={sortKey === SORT_MONTANT ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                  >
                    <button
                      type="button"
                      className={
                        "profile-historique-achats-sort profile-historique-achats-sort--numeric" +
                        (sortKey === SORT_MONTANT ? " profile-historique-achats-sort--active" : "")
                      }
                      onClick={() => onTriColonne(SORT_MONTANT)}
                      title={TEXTS.depotsColMontant}
                      aria-label={TEXTS.depotsColMontant}
                    >
                      <span>{TEXTS.depotsColMontant}</span>
                      <span className="profile-historique-achats-sort-icon" aria-hidden="true">
                        <span
                          className={
                            "profile-historique-achats-sort-icon-char" +
                            (sortKey === SORT_MONTANT ? "" : " profile-historique-achats-sort-icon-char--reserve")
                          }
                        >
                          {sortKey === SORT_MONTANT ? (sortDir === "asc" ? "\u25B2" : "\u25BC") : "\u25BC"}
                        </span>
                      </span>
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {lignesTriees.map((row) => (
                  <tr key={row.id}>
                    <td>{row.noConfirmation}</td>
                    <td>{row.noDepot || "—"}</td>
                    <td>{formatDepotDate(row.dateRaw, language)}</td>
                    <td className="profile-historique-achats-cell-numeric">{formatMontant(row.montant, language)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
